import { useEffect, useCallback } from 'react';
import { useSocketEvent, useSocketConnection } from './useSocketEvent';
import { useSocket } from '../contexts/SocketContext';
import useTaskStore from '../stores/taskStore';

/**
 * Optimized hook for real-time updates with smart reconciliation
 * Instead of refreshing all data, it applies specific updates
 * and checks for pending operations to avoid conflicts
 * 
 * @param {string} projectId - Current project ID to filter updates
 * @param {Function} refreshTasks - Fallback function to reload tasks (only used when necessary)
 * @param {Function} refreshSections - Fallback function to reload sections (only used when necessary)
 */
export const useProjectRealtimeOptimized = (projectId, refreshTasks, refreshSections) => {
  const connected = useSocketConnection();
  const { socket } = useSocket();
  
  // Get store methods
  const { 
    projectTasks,
    projectSections,
    clearCalendarCache
  } = useTaskStore();
  
  // Helper to invalidate calendar cache when tasks change
  const invalidateCalendarCache = useCallback(() => {
    if (projectId) {
      console.log('[RealtimeOptimized] Invalidating calendar cache for project:', projectId);
      clearCalendarCache(projectId);
    }
  }, [projectId, clearCalendarCache]);
  
  // Join/leave project room when projectId changes
  useEffect(() => {
    if (socket && projectId && connected) {
      console.log('[RealtimeOptimized] Joining project room:', projectId);
      socket.emit('join:project', projectId);
      
      const handleJoined = (data) => {
        console.log('[RealtimeOptimized] Successfully joined project room:', data.projectId);
      };
      socket.on('joined:project', handleJoined);
      
      return () => {
        console.log('[RealtimeOptimized] Leaving project room:', projectId);
        socket.emit('leave:project', projectId);
        socket.off('joined:project', handleJoined);
      };
    }
  }, [projectId, connected, socket]);

  // Helper to apply task update to store
  const applyTaskUpdate = useCallback((task) => {
    useTaskStore.setState((state) => {
      const tasks = state.projectTasks[projectId] || [];
      const taskIndex = tasks.findIndex(t => t._id === task._id);
      
      if (taskIndex !== -1) {
        const updatedTasks = [...tasks];
        updatedTasks[taskIndex] = { ...updatedTasks[taskIndex], ...task };
        
        return {
          projectTasks: {
            ...state.projectTasks,
            [projectId]: updatedTasks
          },
          lastUpdate: Date.now()
        };
      }
      return state;
    });
  }, [projectId]);

  // Helper to add new task to store
  const addTaskToStore = useCallback((task) => {
    useTaskStore.setState((state) => {
      const tasks = state.projectTasks[projectId] || [];
      
      // Check if task already exists
      const existingTask = tasks.find(t => t._id === task._id);
      if (existingTask) {
        return state;
      }
      
      const updatedTasks = [...tasks, task];
      
      // Update section count
      const sectionPagination = { ...state.sectionPagination };
      if (sectionPagination[projectId]) {
        const projectPagination = { ...sectionPagination[projectId] };
        const sectionKey = task.sectionId || null;
        
        if (projectPagination[sectionKey]) {
          projectPagination[sectionKey] = {
            ...projectPagination[sectionKey],
            total: (projectPagination[sectionKey].total || 0) + 1
          };
        }
        
        sectionPagination[projectId] = projectPagination;
      }
      
      return {
        projectTasks: {
          ...state.projectTasks,
          [projectId]: updatedTasks
        },
        sectionPagination,
        lastUpdate: Date.now()
      };
    });
  }, [projectId]);

  // Helper to remove task from store
  const removeTaskFromStore = useCallback((taskId) => {
    useTaskStore.setState((state) => {
      const tasks = state.projectTasks[projectId] || [];
      const taskToRemove = tasks.find(t => t._id === taskId);
      const updatedTasks = tasks.filter(t => t._id !== taskId);
      
      // Update section count
      const sectionPagination = { ...state.sectionPagination };
      if (taskToRemove && sectionPagination[projectId]) {
        const projectPagination = { ...sectionPagination[projectId] };
        const sectionKey = taskToRemove.sectionId || null;
        
        if (projectPagination[sectionKey]) {
          projectPagination[sectionKey] = {
            ...projectPagination[sectionKey],
            total: Math.max(0, (projectPagination[sectionKey].total || 0) - 1)
          };
        }
        
        sectionPagination[projectId] = projectPagination;
      }
      
      return {
        projectTasks: {
          ...state.projectTasks,
          [projectId]: updatedTasks
        },
        sectionPagination,
        lastUpdate: Date.now()
      };
    });
  }, [projectId]);

  // Task created event
  useSocketEvent('task:created', (data) => {
    console.log('[RealtimeOptimized] task:created event received:', data);
    
    if (projectId && data.task?.projectId === projectId) {
      // Add the new task to the store
      console.log('[RealtimeOptimized] Adding new task from socket event');
      addTaskToStore(data.task);
      
      // Invalidate calendar cache if task has due date
      if (data.task.dueDate) {
        invalidateCalendarCache();
      }
    }
  });

  // Task updated event
  useSocketEvent('task:updated', (data) => {
    console.log('[RealtimeOptimized] task:updated event received:', data);
    
    if (projectId && data.task?.projectId === projectId) {
      // Apply the update
      console.log('[RealtimeOptimized] Applying task update from socket event');
      applyTaskUpdate(data.task);
      
      // Invalidate calendar cache if task has due date
      if (data.task.dueDate) {
        invalidateCalendarCache();
      }
    }
  });

  // Task deleted event
  useSocketEvent('task:deleted', (data) => {
    console.log('[RealtimeOptimized] task:deleted event received:', data);
    
    if (projectId && data.projectId === projectId) {
      // Remove the task
      console.log('[RealtimeOptimized] Removing task from socket event');
      removeTaskFromStore(data.taskId);
      
      // Invalidate calendar cache
      invalidateCalendarCache();
    }
  });

  // Task moved event (between sections)
  useSocketEvent('task:moved', (data) => {
    console.log('[RealtimeOptimized] task:moved event received:', data);
    
    if (projectId && data.task?.projectId === projectId) {
      // For moves, we need to refresh to get correct ordering
      console.log('[RealtimeOptimized] Task moved, refreshing tasks');
      refreshTasks();
    }
  });

  // Task reordered event (within section)
  useSocketEvent('task:reordered', (data) => {
    console.log('[RealtimeOptimized] task:reordered event received:', data);
    
    if (projectId && data.projectId === projectId) {
      // For reorders, refresh to get correct ordering
      console.log('[RealtimeOptimized] Tasks reordered, refreshing');
      refreshTasks();
    }
  });

  // Task status changed
  useSocketEvent('task:status_changed', (data) => {
    console.log('[RealtimeOptimized] task:status_changed event received:', data);
    
    if (projectId && data.task?.projectId === projectId) {
      // Apply status update
      applyTaskUpdate({ _id: data.task._id, status: data.task.newStatus });
    }
  });

  // Task priority changed
  useSocketEvent('task:priority_changed', (data) => {
    console.log('[RealtimeOptimized] task:priority_changed event received:', data);
    
    if (projectId && data.task?.projectId === projectId) {
      // Apply priority update
      applyTaskUpdate({ _id: data.task._id, priority: data.task.newPriority });
    }
  });

  // Task due date changed
  useSocketEvent('task:due_date_changed', (data) => {
    console.log('[RealtimeOptimized] task:due_date_changed event received:', data);
    
    if (projectId && data.task?.projectId === projectId) {
      
      // Apply due date update
      applyTaskUpdate({ _id: data.task._id, dueDate: data.task.newDueDate });
      
      // Always invalidate calendar cache for due date changes
      invalidateCalendarCache();
    }
  });

  // Task assigned/unassigned
  useSocketEvent('task:assigned', (data) => {
    console.log('[RealtimeOptimized] task:assigned event received:', data);
    
    if (data.task) {
      
      // For assignment changes, refresh to get updated assignee data
      refreshTasks();
    }
  });

  useSocketEvent('task:unassigned', (data) => {
    console.log('[RealtimeOptimized] task:unassigned event received:', data);
    
    if (data.task) {
      
      // For assignment changes, refresh to get updated assignee data
      refreshTasks();
    }
  });

  // Progress updates (checklist changes)
  useSocketEvent('task:progress_updated', (data) => {
    console.log('[RealtimeOptimized] task:progress_updated event received:', data);
    
    if (projectId && data.task?.projectId === projectId) {
      
      // Apply progress update
      applyTaskUpdate({
        _id: data.task._id,
        completionPercentage: data.task.completionPercentage,
        checklist: data.task.checklist
      });
    }
  });

  // Section events (only relevant for project views)
  useSocketEvent('section:created', (data) => {
    if (!projectId || data.section?.projectId !== projectId) return;
    
    console.log('[RealtimeOptimized] Remote section created, refreshing sections');
    refreshSections();
  });

  useSocketEvent('section:updated', (data) => {
    if (!projectId || data.section?.projectId !== projectId) return;
    
    console.log('[RealtimeOptimized] Remote section updated, refreshing sections');
    refreshSections();
  });

  useSocketEvent('section:deleted', (data) => {
    if (!projectId || data.projectId !== projectId) return;
    
    console.log('[RealtimeOptimized] Remote section deleted, refreshing both');
    refreshSections();
    refreshTasks(); // Tasks might have been affected
  });

  useSocketEvent('section:reordered', (data) => {
    if (!projectId || data.projectId !== projectId) return;
    
    console.log('[RealtimeOptimized] Remote sections reordered, refreshing sections');
    refreshSections();
  });

  // Attachment events
  useSocketEvent('task:attachment_uploaded', (data) => {
    console.log('[RealtimeOptimized] task:attachment_uploaded event received:', data);
    
    if (projectId && data.task?.projectId === projectId) {
      
      // For attachment changes, refresh the specific task
      refreshTasks();
    }
  });

  useSocketEvent('task:attachment_deleted', (data) => {
    console.log('[RealtimeOptimized] task:attachment_deleted event received:', data);
    
    if (projectId && data.task?.projectId === projectId) {
      
      // For attachment changes, refresh the specific task
      refreshTasks();
    }
  });

  // Log connection status
  useEffect(() => {
    if (!projectId) return;
    console.log(`[RealtimeOptimized] Project ${projectId} real-time updates initialized`);
    
    return () => {
      console.log(`[RealtimeOptimized] Project ${projectId} real-time updates cleanup`);
    };
  }, [projectId]);
};